import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlarmClock, ShieldOff, Phone, Mail, Globe, X } from 'lucide-react';
import { PlayUnlockBtn } from './LicenseParts';
import { expiringLicenses, getBlocked, clearBlocked, getContact, getDeviceId, demoInfo, hasModule, PACK_PRICE } from '../lib/license';

const fill = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k] ?? '');
const DISMISS_KEY = 'giss_remind_dismissed';

function ContactBox({ t }) {
  const c = getContact();
  return (
    <div className="lic-contact" data-testid="license-contact">
      <small>{t.renewHint}</small>
      <b data-testid="license-contact-name">{c.name}</b>
      {c.phone && <a href={`tel:${c.phone.replace(/\s/g, '')}`} data-testid="license-contact-phone"><Phone size={13} /> {c.phone}</a>}
      {c.email && <a href={`mailto:${c.email}?subject=${encodeURIComponent('Lizenz ' + getDeviceId())}`} data-testid="license-contact-email"><Mail size={13} /> {c.email}</a>}
      {c.web && <a href={c.web.startsWith('http') ? c.web : `https://${c.web}`} target="_blank" rel="noreferrer" data-testid="license-contact-web"><Globe size={13} /> {c.web}</a>}
      <span className="mono" data-testid="license-contact-device">{t.deviceId}: {getDeviceId()}</span>
    </div>
  );
}

const modNames = (t, l) => l.mods.map((m) => t['mod_' + m]).join(', ');

export function ExpiryBanner({ t, onOpen }) {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const lics = expiringLicenses(7);
  const demo = demoInfo();
  const demoOver = !demo.active && !hasModule('pro') && !lics.length;
  if (demoOver) {
    return (
      <div className="lic-banner bad" data-testid="license-locked-banner">
        <AlarmClock size={18} />
        <div className="lic-banner-body"><p>{demo.used ? t.needLicense : t.demoOver}</p><div className="row"><PlayUnlockBtn t={t} small testid="banner-play-unlock-btn" onChange={onOpen} /><button className="btn btn-dark btn-sm" data-testid="license-open-btn" onClick={onOpen}>{t.licOpen}</button></div></div>
      </div>
    );
  }
  if (!hidden && demo.active) {
    return (
      <div className="lic-banner trial" data-testid="license-trial-banner">
        <AlarmClock size={18} />
        <div className="lic-banner-body">
          <p data-testid="license-trial-text">⏳ {fill(t.trialBanner, { days: demo.daysLeft })}</p>
          <div className="row"><PlayUnlockBtn t={t} small testid="banner-play-unlock-btn" onChange={onOpen} /></div>
        </div>
        <button className="icon-btn" data-testid="license-trial-dismiss" aria-label={t.dismiss} onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setHidden(true); }}><X size={15} /></button>
      </div>
    );
  }
  if (hidden || !lics.length) return null;
  const msg = (l) => {
    const left = Math.ceil((l.exp - Date.now()) / 864e5);
    if (l.expired) return fill(t.licYearExpired, { price: PACK_PRICE.ALL.toFixed(2).replace('.', ','), date: new Date(l.exp).toLocaleDateString(t.locale) });
    if (left <= 0) return fill(t.remindToday, { mods: modNames(t, l) });
    return fill(t.remindSoon, { mods: modNames(t, l), days: left, date: new Date(l.exp).toLocaleDateString(t.locale) });
  };
  return (
    <div className="lic-banner" data-testid="license-expiry-banner">
      <AlarmClock size={18} />
      <div className="lic-banner-body">
        {lics.map((l) => <p key={l.code} data-testid="license-expiry-text">{msg(l)}</p>)}
        <button className="btn btn-dark btn-sm" data-testid="license-open-btn" onClick={onOpen}>{t.licOpen}</button>
      </div>
      <button className="icon-btn" data-testid="license-expiry-dismiss" aria-label={t.dismiss} onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setHidden(true); }}><X size={15} /></button>
    </div>
  );
}

export function BlockedDialog({ t, onClose }) {
  const blocked = getBlocked();
  if (!blocked.length) return null;
  const close = () => { clearBlocked(); onClose(); };
  return createPortal(
    <div className="editor-backdrop" data-testid="license-blocked-dialog">
      <div className="editor lic-blocked">
        <div className="editor-head">
          <h4><ShieldOff size={18} /> {t.blockedTitle}</h4>
        </div>
        {blocked.map((b) => (
          <p key={b.code} data-testid="license-blocked-text">{fill(t.blockedText, { reason: t['reason_' + b.reason] || b.reason, mods: modNames(t, b) })} <span className="mono">({b.code})</span></p>
        ))}
        <ContactBox t={t} />
        <div className="row"><button className="btn btn-dark" data-testid="license-blocked-ok" onClick={close}>{t.understood}</button></div>
      </div>
    </div>,
    document.body,
  );
}
